import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Alert } from '@material-ui/lab';

import { post } from '../../../services/api';

import { AppContext } from '../../../contexts/AppContext';
import {
  ContribuicaoContext,
  ContribuicaoState,
} from '../../../contexts/ContribuicaoContext';
import {
  DadosCadastraisContext,
  DadosCadastraisState,
  ITermo,
} from '../../../contexts/DadosCadastraisContext';

import MuiButton from '../../../components/MuiButton';
import Loading from '../../../components/Loading';

import useRouter from '../../../utils/hooks/useRouter';
import detectaDispositivo from '../../../utils/detectaDispositivo';
import getTermo from '../../../utils/getTermo';
import getMesAtual from '../../../utils/getMesAtual';

import { Title, Text } from '../../../styles/global';

interface IBodyContribuicao {
  idPessoa: number | null;
  cooperador: boolean | null;
  contribuicao: ContribuicaoState;
  pessoa: Omit<DadosCadastraisState, 'idPessoa' | 'cooperador'>;
}

interface TermoProps extends ITermo {
  texto?: string;
}

function conversaoMsDias(ms: number) {
  return ms / (1000 * 60 * 60 * 24);
}

function Termo() {
  const router = useRouter();

  const [erro, setErro] = useState<string>('');
  const [termo, setTermo] = useState<TermoProps>();

  const { appState, appDispatch } = useContext(AppContext);
  const { contribuicao } = useContext(ContribuicaoContext);
  const { dadosCadastrais, dadosCadastraisDispatch } = useContext(DadosCadastraisContext);

  const {
    tipo,
    meioDePagamento,
    dataInicio,
    valorContribuicao,
  } = contribuicao;

  const {
    idPessoa,
    cooperador,
    endereco,
    contatos,
    termos,
  } = dadosCadastrais;
  const { celular } = dadosCadastrais.contatos;
  const { nome, cpf, dataNascimento } = dadosCadastrais.dados;

  /** Requisicao para retornar o termo e guardar no estado termo */
  const recebeTermo = useCallback(async () => {
    setTermo(await getTermo(tipo === 1 ? 6 : 5));
  }, []);

  /** Monitora estado termo, ao ser alterado, atualiza o state termos */
  useEffect(() => {
    if (termo) {
      const termoAceito = {
        idTermo: termo.idTermo,
        idTermoTipo: termo.idTermoTipo,
        dispositivo: detectaDispositivo(),
        aceito: true,
      };

      const verificaTermosDuplicados = termos.filter(
        (term: any) => term.idTermo !== termoAceito.idTermo,
      );

      dadosCadastraisDispatch({
        type: 'ALTERA_TERMOS',
        termos: [...verificaTermosDuplicados, termoAceito],
      });
    }
  }, [termo]);

  /**
   * Ao carregar componente atualiza etapa atual e url anterior
   * Inicia a instância do Checkout da pagarme
   */
  useEffect(() => {
    appDispatch({ type: 'ALTERA_ETAPA_ATUAL', payload: 5 });
    appDispatch({ type: 'MANIPULA_DISABLED', payload: false });
    appDispatch({ type: 'MANIPULA_CARREGANDO', payload: false });
    appDispatch({
      type: 'ALTERA_URL_ANTERIOR',
      payload: '/formulario/4/dados-pessoais/endereco',
    });
    recebeTermo();
  }, []);

  const formataMeioDePagamento = useMemo(
    () => (meioDePagamento === 3 ? 'boleto' : 'credit_card'),
    [],
  );
  const formataValorDaContribuicao = useMemo(() => {
    if (tipo === 2 && meioDePagamento === 5) {
      return 100;
    }
    return valorContribuicao * 100;
  }, []);

  /**
   * Ao usuario aceitar o termo
   */
  const aceitaTermo = useCallback(async () => {
    window.sessionStorage.setItem('tentativa-concluida', 'true');
    appDispatch({ type: 'MANIPULA_CARREGANDO', payload: true });
    const body: IBodyContribuicao = {
      idPessoa,
      cooperador,
      contribuicao,
      pessoa: {
        app: 'App Contribua',
        dados: {
          cpf: cpf.replace(/[^0-9]+/g, ''),
          nome,
          dataNascimento,
        },
        contatos: {
          celular,
          email: {
            tipo: contatos.email?.tipo,
            endereco: contatos.email?.endereco,
          },
        },
        endereco,
        termos,
      },
    };

    try {
      const retorno = await post('api-contribua', '/contribuicoes', body);
      if (retorno?.status === 200) {
        // const { idContribuicao } = retorno.data.objInserido;
        const { pedidoOdoo } = retorno.data;

        window.location.replace(pedidoOdoo.checkout);
      } else setErro('');
    } catch (error) {
      window.sessionStorage.removeItem('tentativa-concluida');
      if (error.data && typeof error.data.mensagem === 'string') {
        setErro(error.data.mensagem);
      } else {
        setErro('Oops... Ocorreu um erro inesperado, tente novamente!');
      }
      setTimeout(() => {
        setErro('');
      }, 4000);
      appDispatch({ type: 'MANIPULA_CARREGANDO', payload: false });
    }
  }, [termos]);

  const getDataEspecifica = useCallback((data: string) => {
    if (dataInicio) {
      const dataFormatada = dataInicio?.split('-');
      switch (data) {
        case 'ANO':
          return dataFormatada[0];
        case 'MES':
          return getMesAtual(parseInt(dataFormatada[1], 10) - 1);
        case 'DIA':
          return dataFormatada[2];
        default:
          return 'Informe um parâmetro válido';
      }
    }
    return false;
  }, []);

  return (
    <>
      {appState.carregando && (
        <Loading
          text="Aguarde um momento... estamos concluindo sua contribuição."
          size={60}
          full
        />
      )}
      <Title
        secondary="true"
        gutterBottom
        align="left"
        style={{ width: '100%' }}
      >
        Autorização de contribuição
      </Title>
      {termo ? (
        <Text align="left" gutterBottom size="2.2rem" weight="400">
          {termo.texto}
        </Text>
      ) : (
        <Loading size={60} full />
      )}
      {tipo === 2 && meioDePagamento === 5 && (
        <Text gutterBottom weight="400" align="left" size="2.2rem">
          Ao concluir faremos a reserva de um real para validação do seu cartão
          de crédito, mas o valor não será cobrado em sua fatura. A primeira
          parcela será cobrada apenas no dia {getDataEspecifica('DIA')} de{' '}
          {getDataEspecifica('MES')} de {getDataEspecifica('ANO')}.
        </Text>
      )}
      <Text gutterBottom weight="700" align="left" size="2.2rem">
        Ao continuar você estará de acordo.
      </Text>
      <MuiButton
        primary="true"
        fullWidth
        disabled={appState.disabled}
        onClick={aceitaTermo}
      >
        Aceitar e Continuar
      </MuiButton>
      {erro && (
        <Alert severity="error" style={{ fontSize: '2rem' }}>
          {erro}
        </Alert>
      )}
    </>
  );
}

export default Termo;
